import { InfoOutlineIcon } from '@chakra-ui/icons';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Flex,
  SkeletonText,
  Text,
  Tooltip,
  InputGroup,
  Input,
  List,
  ListItem,
} from '@chakra-ui/react';
import { useGetAppEventsQuery } from 'store/campaigns/adsetFormApi';

function AppEventsAdspresso({
  adsAccountId,
  adSetFormik = {},
  onIncludeAppEventChange,
  context = '',
}) {
  const inputRef = useRef(null);
  const popupRef = useRef(null);

  const [showPopup, setShowPopup] = useState(false);
  const [appEventOptions, setAppEventOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAppOptions, setSelectedAppOptions] = useState({});

  // Derive current promoted_object for this ads account
  const candidateMatching = useMemo(() => {
    if (context === 'trackEdit') return adSetFormik?.promoted_object;
    return adSetFormik?.promoted_object?.find?.(
      (obj) => obj?.adsAccountId === adsAccountId
    );
  }, [adSetFormik?.promoted_object, adsAccountId, context]);

  // Freeze app_id and initial custom_event_type; only update when app_id actually changes.
  const [stableAppId, setStableAppId] = useState(
    candidateMatching?.application_id ?? null
  );
  const [stableInitialCustomEventType, setStableInitialCustomEventType] =
    useState(candidateMatching?.custom_event_type ?? '');

  useEffect(() => {
    const nextAppId = candidateMatching?.application_id ?? null;
    if (nextAppId !== stableAppId) {
      setStableAppId(nextAppId);
      setStableInitialCustomEventType(candidateMatching?.custom_event_type ?? '');
      // Reset UI on real app switch
      setSelectedAppOptions({});
      setSearchTerm('');
      setAppEventOptions([]);
      setShowPopup(false);
      onIncludeAppEventChange?.({});
    }
  }, [candidateMatching?.application_id]);

  const {
    data: appEvents,
    isLoading: isAppEventLoading,
    isFetching,
  } = useGetAppEventsQuery(
    { adsAccountId, app_id: stableAppId },
    { skip: !stableAppId }
  );

  // Filtering & auto-select logic:
  // - The LIST should show ALL appEvents by default.
  // - Only when user types (searchTerm > 0), show the filtered list.
  // - Still auto-select the initial type (if provided) ONCE, but do NOT filter the list for that.
  useEffect(() => {
    if (!appEvents) return;

    if (searchTerm?.length > 0) {
      const filtered = appEvents?.filter((app) =>
        app?.lable?.toLowerCase?.().includes(searchTerm.toLowerCase())
      );
      setAppEventOptions(filtered || []);
      setShowPopup((filtered?.length ?? 0) > 0);
    } else {
      // Always show ALL options when not searching
      setAppEventOptions(appEvents || []);
      // Auto-select initial only if nothing picked yet
      if (stableInitialCustomEventType && !selectedAppOptions?.value) {
        const initial =
          appEvents?.find((app) =>
            app?.value
              ?.toLowerCase?.()
              .includes(stableInitialCustomEventType.toLowerCase())
          ) || {};
        if (initial?.value) {
          setSelectedAppOptions(initial);
          setTimeout(() => onIncludeAppEventChange?.(initial), 0);
        }
      }
    }
  }, [searchTerm, appEvents, stableInitialCustomEventType, selectedAppOptions?.value]);

  const handleAppEventSelect = (app) => {
    const picked = app || {};
    setSelectedAppOptions(picked);
    setTimeout(() => onIncludeAppEventChange?.(picked), 0);
    setSearchTerm('');
    setShowPopup(false);
    // restore full list after picking
    setAppEventOptions(appEvents || []);
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const inputValue = selectedAppOptions?.lable ?? searchTerm;

  return (
    <Flex direction="column" gap={2} w="100%">
      <Flex alignItems="center">
        <Text fontWeight="bold">App event</Text>
        <Tooltip
          borderRadius={5}
          label={
            <Flex fontSize="14px" p="5px" direction="column" gap={2}>
              <Text fontWeight="bold">About app events</Text>
              <Text fontSize="12px">
                App events are the actions people take while using your app, such
                as making a purchase or achieving a new level in a game.
              </Text>
            </Flex>
          }
          fontSize="md"
        >
          <InfoOutlineIcon pointerEvents="all" ml={2} />
        </Tooltip>
      </Flex>

      {isAppEventLoading || isFetching ? (
        <Flex>
          <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
        </Flex>
      ) : (
        <Box position="relative">
          <Flex>
            <InputGroup>
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Choose or Search for an app event"
                borderRadius="md"
                size="sm"
                isDisabled={!stableAppId && (!appEvents || appEvents?.length === 0)}
                onFocus={() => {
                  if (appEvents?.length > 0) {
                    // Show ALL on focus
                    setAppEventOptions(appEvents || []);
                    setShowPopup(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Backspace' &&
                    selectedAppOptions?.lable &&
                    searchTerm === ''
                  ) {
                    handleAppEventSelect({});
                    if (appEvents?.length > 0) {
                      setAppEventOptions(appEvents);
                      setShowPopup(true);
                    }
                  }
                }}
              />
            </InputGroup>
          </Flex>

          <Flex mt={2}>
            {showPopup && (appEventOptions?.length ?? 0) > 0 && (
              <List
                ref={popupRef}
                position="absolute"
                top="100%"
                width="100%"
                border="1px solid #ccc"
                backgroundColor="white"
                zIndex="1000"
                maxHeight="220px"
                overflowY="auto"
                borderRadius="md"
              >
                {appEventOptions.map((app, index) => (
                  <ListItem
                    key={`${app?.value ?? 'val'}-${index}`}
                    p="6px"
                    px="10px"
                    cursor="pointer"
                    _hover={{ backgroundColor: '#f0f0f0' }}
                    onClick={() => handleAppEventSelect(app)}
                  >
                    <Flex gap={2} align="center">
                      <Text fontSize="14px">{app?.lable}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            )}
          </Flex>
        </Box>
      )}
    </Flex>
  );
}

export default AppEventsAdspresso;
